import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Mail, Building2, Briefcase, MapPin, Clock } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const statCards = [
  { icon: Building2, label: "Companies", value: "4", color: "text-primary" },
  { icon: Clock, label: "Years Experience", value: "18+", color: "text-accent" },
  { icon: Briefcase, label: "Current Role", value: "Advocate", color: "text-primary" },
  { icon: MapPin, label: "Location", value: "Hailey, ID", color: "text-accent" },
];

export const HeroSection = ({ profile }) => {
  const handleContactClick = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="min-h-[92vh] flex items-center relative overflow-hidden" data-testid="hero-section" aria-label="Hero - Tommy Green">
      <div className="hero-atmosphere" />
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6 py-24 md:py-0 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Logo + Name + CTAs */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="flex flex-col items-start"
          >
            <motion.div variants={reveal} className="mb-8">
              <div className="relative inline-block">
                <Card className="p-4 bg-card/80 border-border inline-block" style={{ boxShadow: 'var(--shadow-glow-pink)' }}>
                  <img
                    src={profile.logo_url}
                    alt="TYLEGN Logo"
                    className="w-32 h-32 md:w-40 md:h-40 object-contain logo-float"
                    data-testid="hero-logo"
                    width="160"
                    height="160"
                    fetchpriority="high"
                  />
                </Card>
              </div>
            </motion.div>

            <motion.div variants={reveal}>
              <span className="mono-label text-primary mb-3 block">Entrepreneur & Leader</span>
            </motion.div>

            <motion.h1
              variants={reveal}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] mb-6"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Tommy Green
            </motion.h1>

            <motion.p
              variants={reveal}
              className="text-base sm:text-lg text-muted-foreground max-w-lg mb-8 leading-[1.55]"
            >
              {profile.profile_summary.split('.').slice(0, 2).join('.') + '.'}
            </motion.p>

            <motion.div variants={reveal} className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                size="lg"
                className="bg-secondary text-secondary-foreground border border-border hover:border-primary/50"
                onClick={handleContactClick}
                data-testid="hero-contact-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                Get in Touch
              </Button>
            </motion.div>
          </motion.div>

          {/* Right: Bento Stats */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-2 gap-4"
          >
            {statCards.map((stat, i) => (
              <motion.div key={stat.label} variants={reveal}>
                <Card className="p-6 bg-card border-border glow-card cursor-default" data-testid={`hero-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color} mb-3`} />
                  <div className="mono-label text-muted-foreground mb-1">{stat.label}</div>
                  <div className="text-2xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                    {stat.value}
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
